import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  getCountFromServer,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

const WITHDRAW_ACTIVE_STATUSES = ["pending", "under_review", "approved"];
const REPORT_ACTIVE_STATUSES = ["pending", "open", "under_review"];
const TICKET_ACTIVE_STATUSES = ["open", "pending", "under_review"];

const MIN_ELIGIBILITY_TOKENS = 2000;

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function matchesWithdrawEligibilityCard(user) {
  const gender = String(user?.gender || "").trim().toLowerCase();
  const verificationStatus = String(user?.verificationStatus || "")
    .trim()
    .toLowerCase();
  const availableTokens = toNumber(user?.earnings?.availableTokens, 0);
  const weStatus = String(user?.withdrawEligibility?.status || "")
    .trim()
    .toLowerCase();

  return (
    gender === "female" &&
    verificationStatus === "approved" &&
    availableTokens >= MIN_ELIGIBILITY_TOKENS &&
    weStatus !== "approved_for_payout"
  );
}

async function fetchDashboardStats() {
  const usersRef = collection(db, "users");
  const withdrawalsActive = query(
    collection(db, "withdrawals"),
    where("status", "in", WITHDRAW_ACTIVE_STATUSES)
  );
  const verificationPending = query(
    collection(db, "verificationRequests"),
    where("status", "==", "pending")
  );
  const reportsActive = query(
    collection(db, "reports"),
    where("status", "in", REPORT_ACTIVE_STATUSES)
  );
  const ticketsActive = query(
    collection(db, "supportTickets"),
    where("status", "in", TICKET_ACTIVE_STATUSES)
  );

  const [
    withdrawRequestsSnap,
    verificationSnap,
    reportsSnap,
    ticketsSnap,
    usersSnap,
  ] = await Promise.all([
    getCountFromServer(withdrawalsActive),
    getCountFromServer(verificationPending),
    getCountFromServer(reportsActive),
    getCountFromServer(ticketsActive),
    getDocs(usersRef),
  ]);

  let withdrawEligibility = 0;
  usersSnap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    if (matchesWithdrawEligibilityCard(data)) withdrawEligibility += 1;
  });

  return {
    withdrawRequests: withdrawRequestsSnap.data().count,
    withdrawEligibility,
    verificationQueue: verificationSnap.data().count,
    users: usersSnap.size,
    reports: reportsSnap.data().count,
    supportTickets: ticketsSnap.data().count,
  };
}

const EMPTY_STATS = {
  withdrawRequests: 0,
  withdrawEligibility: 0,
  verificationQueue: 0,
  users: 0,
  reports: 0,
  supportTickets: 0,
};

export default function DashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const next = await fetchDashboardStats();
        if (!cancelled) setStats(next);
      } catch (err) {
        console.error("DashboardPage stats error:", err);
        if (!cancelled) setStats(EMPTY_STATS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const cards = [
    {
      key: "withdrawRequests",
      title: t("admin.support.dashboard.withdraw_requests", { defaultValue: "Withdraw Requests" }),
      value: stats.withdrawRequests,
      note: t("admin.support.dashboard.withdraw_requests_note", {
        defaultValue: "Pending, under review, or approved",
      }),
    },
    {
      key: "withdrawEligibility",
      title: t("admin.support.dashboard.withdraw_eligibility", { defaultValue: "Withdraw Eligibility" }),
      value: stats.withdrawEligibility,
      note: t("admin.support.dashboard.withdraw_eligibility_note", {
        defaultValue: "Female, verified, ≥ {{tokens}} tokens, not approved for payout",
        tokens: MIN_ELIGIBILITY_TOKENS,
      }),
    },
    {
      key: "verificationQueue",
      title: t("admin.support.dashboard.verification_queue", { defaultValue: "Verification Queue" }),
      value: stats.verificationQueue,
      note: t("admin.support.dashboard.verification_queue_note", {
        defaultValue: "Pending verification reviews",
      }),
    },
    {
      key: "users",
      title: t("admin.support.dashboard.users", { defaultValue: "Users" }),
      value: stats.users,
      note: t("admin.support.dashboard.users_note", { defaultValue: "Total users" }),
    },
    {
      key: "reports",
      title: t("admin.support.dashboard.reports", { defaultValue: "Reports" }),
      value: stats.reports,
      note: t("admin.support.dashboard.reports_note", {
        defaultValue: "Pending, open, or under review",
      }),
    },
    {
      key: "supportTickets",
      title: t("admin.support.dashboard.support_tickets", { defaultValue: "Support Tickets" }),
      value: stats.supportTickets,
      note: t("admin.support.dashboard.support_tickets_note", {
        defaultValue: "Open, pending, or under review",
      }),
    },
  ];

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">
          {t("admin.support.dashboard.title", { defaultValue: "Dashboard" })}
        </h1>
        <p className="admin1-dashboard__subtitle">
          {t("admin.support.dashboard.subtitle", {
            defaultValue: "Livora admin workspace shell is ready.",
          })}
        </p>
      </header>

      {loading ? (
        <div className="admin1-state-card" style={{ marginBottom: 14 }}>
          {t("admin.support.dashboard.loading_stats", {
            defaultValue: "Loading dashboard stats...",
          })}
        </div>
      ) : null}

      <section className="admin1-dashboard__grid">
        {cards.map((card) => (
          <article key={card.key} className="admin1-card">
            <div className="admin1-card__title">{card.title}</div>
            <div
              className={`admin1-card__value${loading ? " admin1-card__value--loading" : ""}`}
            >
              {loading ? "…" : card.value}
            </div>
            <div className="admin1-card__note">{card.note}</div>
          </article>
        ))}
      </section>
    </div>
  );
}
